import {createTheme, assignTokens} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {tokens} from '@sail/tokens/deprecated';
import {LayerConfig} from '../../components/layers/LayerConfig';
import {css} from '../../css';

// Font metrics extracted from Inter 4.1 (InterVariable.ttf) via ttx/fonttools.
// These replace the SF Pro baseline metrics when the Inter theme is active.
const visualRefreshTypographyTheme = {
  'font.family.typeface': `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
  'font.family.unitsPerEm': '2048',
  'font.family.ascender': '1984',
  'font.family.capHeight': '1490',
  'font.family.xHeight': '1118',
  'font.family.descender': '-494',
  'font.family.lineGap': '0',
};

// Font metrics from SFNSMono.ttf (ui-monospace on macOS/iOS) via ttx/fonttools.
// On Windows, ui-monospace resolves to Cascadia Mono which has different metrics (e.g. descender: -600).
// Baseline alignment will be slightly off on Windows, so we'd need to implement per-font metric switching.
// This is a gap we already have on the current monospace font stack, the metrics are computed for the
// Menlo font, which is typically only installed on macOS.
const visualRefreshMonospaceTheme = {
  'font.monospace.family.typeface': `ui-monospace, 'SF Mono', Menlo, Consolas, 'Courier New', monospace`,
  'font.monospace.family.unitsPerEm': '2048',
  'font.monospace.family.ascender': '1980',
  'font.monospace.family.capHeight': '1443',
  'font.monospace.family.xHeight': '1077',
  'font.monospace.family.descender': '-432',
  'font.monospace.family.lineGap': '0',
};

// Remaps light (300) -> (375), bold (700) → semibold (600), propagating to all heading/display tokens built on
// weight.bold.
const visualRefreshWeightTheme = {
  'font.weight.bold': '600',
  'font.weight.light': '375',
};

const visualRefreshTypographySizeTheme = {
  // ─── Display → Headline XL ───────────────────────────────────────────────
  'font.display.xlarge.size': '37px',
  'font.display.xlarge.lineHeight': '40px',
  'font.display.xlarge.weight': '600',
  'font.display.xlarge.letterSpacing': '-0.02em',
  'font.display.xlarge.fontVariationSettings': "'opsz' 32",
  'font.display.xlarge.subdued.size': '37px',
  'font.display.xlarge.subdued.lineHeight': '40px',
  'font.display.xlarge.subdued.weight': '375',
  'font.display.xlarge.subdued.letterSpacing': '-0.02em',
  'font.display.xlarge.subdued.fontVariationSettings': "'opsz' 32",

  'font.display.large.size': '37px',
  'font.display.large.lineHeight': '40px',
  'font.display.large.weight': '600',
  'font.display.large.letterSpacing': '-0.02em',
  'font.display.large.fontVariationSettings': "'opsz' 32",
  'font.display.large.subdued.size': '37px',
  'font.display.large.subdued.lineHeight': '40px',
  'font.display.large.subdued.weight': '375',
  'font.display.large.subdued.letterSpacing': '-0.02em',
  'font.display.large.subdued.fontVariationSettings': "'opsz' 32",

  'font.display.medium.size': '37px',
  'font.display.medium.lineHeight': '40px',
  'font.display.medium.weight': '600',
  'font.display.medium.letterSpacing': '-0.02em',
  'font.display.medium.fontVariationSettings': "'opsz' 32",
  'font.display.medium.subdued.size': '37px',
  'font.display.medium.subdued.lineHeight': '40px',
  'font.display.medium.subdued.weight': '375',
  'font.display.medium.subdued.letterSpacing': '-0.02em',
  'font.display.medium.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Display Small → Headline Large ──────────────────────────────────────
  'font.display.small.size': '28px',
  'font.display.small.lineHeight': '32px',
  'font.display.small.weight': '600',
  'font.display.small.letterSpacing': '-0.015em',
  'font.display.small.fontVariationSettings': "'opsz' 32",
  'font.display.small.subdued.size': '28px',
  'font.display.small.subdued.lineHeight': '32px',
  'font.display.small.subdued.letterSpacing': '-0.015em',
  'font.display.small.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Heading XLarge → Headline Large ─────────────────────────────────────
  'font.heading.xlarge.size': '28px',
  'font.heading.xlarge.lineHeight': '32px',
  'font.heading.xlarge.weight': '600',
  'font.heading.xlarge.letterSpacing': '-0.015em',
  'font.heading.xlarge.fontVariationSettings': "'opsz' 32",
  'font.heading.xlarge.subdued.size': '28px',
  'font.heading.xlarge.subdued.lineHeight': '32px',
  'font.heading.xlarge.subdued.letterSpacing': '-0.015em',
  'font.heading.xlarge.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Heading Large → Headline Large ──────────────────────────────────────
  'font.heading.large.size': '28px',
  'font.heading.large.lineHeight': '32px',
  'font.heading.large.weight': '600',
  'font.heading.large.letterSpacing': '-0.015em',
  'font.heading.large.fontVariationSettings': "'opsz' 32",
  'font.heading.large.subdued.size': '28px',
  'font.heading.large.subdued.lineHeight': '32px',
  'font.heading.large.subdued.letterSpacing': '-0.015em',
  'font.heading.large.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Heading Medium → Headline Medium ────────────────────────────────────
  // Size and line-height unchanged (20/28).
  'font.heading.medium.weight': '600',
  'font.heading.medium.letterSpacing': '-0.01em',
  'font.heading.medium.fontVariationSettings': "'opsz' 32",
  'font.heading.medium.subdued.letterSpacing': '-0.01em',
  'font.heading.medium.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Heading Small → Headline Small ──────────────────────────────────────
  'font.heading.small.size': '15px',
  'font.heading.small.lineHeight': '20px',
  'font.heading.small.weight': '600',
  'font.heading.small.letterSpacing': '-0.005em',
  'font.heading.small.fontVariationSettings': "'opsz' 32",
  'font.heading.small.subdued.size': '15px',
  'font.heading.small.subdued.lineHeight': '20px',
  'font.heading.small.subdued.letterSpacing': '-0.005em',
  'font.heading.small.subdued.fontVariationSettings': "'opsz' 32",

  // ─── Heading XSmall → Body ───────────────────────────────────────────────
  'font.heading.xsmall.size': '13px',
  'font.heading.xsmall.lineHeight': '20px',
  'font.heading.xsmall.weight': '500',
  'font.heading.xsmall.letterSpacing': '-0.005em',
  'font.heading.xsmall.fontVariationSettings': "'opsz' 14",
  'font.heading.xsmall.subdued.size': '13px',
  'font.heading.xsmall.subdued.lineHeight': '20px',
  'font.heading.xsmall.subdued.letterSpacing': '-0.005em',
  'font.heading.xsmall.subdued.fontVariationSettings': "'opsz' 14",

  // ─── Body Large → Body ───────────────────────────────────────────────────
  'font.body.large.size': '13px',
  'font.body.large.lineHeight': '20px',
  'font.body.large.letterSpacing': '-0.005em',
  'font.body.large.fontVariationSettings': "'opsz' 14",
  'font.body.large.emphasized.size': '13px',
  'font.body.large.emphasized.lineHeight': '20px',
  'font.body.large.emphasized.weight': '500',
  'font.body.large.emphasized.letterSpacing': '-0.005em',
  'font.body.large.emphasized.fontVariationSettings': "'opsz' 14",

  // ─── Body Medium → Body ──────────────────────────────────────────────────
  'font.body.medium.size': '13px',
  'font.body.medium.lineHeight': '20px',
  'font.body.medium.letterSpacing': '-0.005em',
  'font.body.medium.fontVariationSettings': "'opsz' 14",
  'font.body.medium.emphasized.size': '13px',
  'font.body.medium.emphasized.lineHeight': '20px',
  'font.body.medium.emphasized.weight': '500',
  'font.body.medium.emphasized.letterSpacing': '-0.005em',
  'font.body.medium.emphasized.fontVariationSettings': "'opsz' 14",

  // ─── Body Small → Body ───────────────────────────────────────────────────
  'font.body.small.size': '13px',
  'font.body.small.lineHeight': '20px',
  'font.body.small.letterSpacing': '-0.005em',
  'font.body.small.fontVariationSettings': "'opsz' 14",
  'font.body.small.emphasized.size': '13px',
  'font.body.small.emphasized.lineHeight': '20px',
  'font.body.small.emphasized.weight': '500',
  'font.body.small.emphasized.letterSpacing': '-0.005em',
  'font.body.small.emphasized.fontVariationSettings': "'opsz' 14",

  // ─── Label Large → Body ──────────────────────────────────────────────────
  'font.label.large.size': '13px',
  'font.label.large.lineHeight': '20px',
  'font.label.large.letterSpacing': '-0.005em',
  'font.label.large.fontVariationSettings': "'opsz' 14",
  'font.label.large.emphasized.size': '13px',
  'font.label.large.emphasized.lineHeight': '20px',
  'font.label.large.emphasized.weight': '500',
  'font.label.large.emphasized.letterSpacing': '-0.005em',
  'font.label.large.emphasized.fontVariationSettings': "'opsz' 14",

  // ─── Label Medium → Body ─────────────────────────────────────────────────
  'font.label.medium.size': '13px',
  'font.label.medium.lineHeight': '20px',
  'font.label.medium.letterSpacing': '-0.005em',
  'font.label.medium.fontVariationSettings': "'opsz' 14",
  'font.label.medium.emphasized.size': '13px',
  'font.label.medium.emphasized.lineHeight': '20px',
  'font.label.medium.emphasized.weight': '500',
  'font.label.medium.emphasized.letterSpacing': '-0.005em',
  'font.label.medium.emphasized.fontVariationSettings': "'opsz' 14",

  // ─── Label Small → Body Small ────────────────────────────────────────────
  'font.label.small.size': '11px',
  'font.label.small.lineHeight': '16px',
  'font.label.small.letterSpacing': '-0.005em',
  'font.label.small.fontVariationSettings': "'opsz' 14",
  'font.label.small.emphasized.size': '11px',
  'font.label.small.emphasized.lineHeight': '16px',
  'font.label.small.emphasized.weight': '500',
  'font.label.small.emphasized.letterSpacing': '-0.005em',
  'font.label.small.emphasized.fontVariationSettings': "'opsz' 14",
} satisfies Partial<Record<keyof typeof $, string>>;

/**
 * Visual refresh typography tokens, inherited family and metrics, and layer
 * defaults.
 */
export const visualRefreshTypography = createTheme(
  {
    name: 'Visual Refresh Typography',
    description:
      'Typography tokens, inherited family and metrics, and layer defaults for the visual refresh.',
    category: 'Visual Refresh',
    internal: true,
  },
  [
    assignTokens($, {
      ...visualRefreshTypographyTheme,
      ...visualRefreshMonospaceTheme,
      ...visualRefreshWeightTheme,
      ...visualRefreshTypographySizeTheme,
    }),
    css({fontFamily: tokens.font.family}),
    LayerConfig.customize({css: {fontFamily: tokens.font.family}}),
  ],
);
